import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import html2canvas from 'html2canvas'
import {
  Activity,
  Award,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Coins,
  Download,
  FileText,
  HeartPulse,
  Info,
  LineChart,
  ReceiptText,
  RefreshCw,
  Search,
  ShieldCheck,
  Stethoscope,
  UsersRound,
  X,
} from 'lucide-react'
import { api } from '../lib/api'
import { formatWon } from '../lib/hiddenInsuranceMoneyUtils'
import { useToast } from '../ui/ToastProvider'

const PROVIDERS = [
  { value: '', label: '전체' },
  { value: 'MEDICAL', label: '의과' },
  { value: 'ORIENTAL', label: '한방' },
]

const VISIT_TYPES = [
  { value: '', label: '전체' },
  { value: 'INPATIENT', label: '입원' },
  { value: 'OUTPATIENT', label: '외래' },
]

const SORTS = [
  { value: 'rank', label: '전국 순위' },
  { value: 'patientCount', label: '환자 수' },
  { value: 'visitCount', label: '진료 건수' },
  { value: 'totalCost', label: '총진료비' },
  { value: 'avgCost', label: '1인 평균' },
]

const FALLBACK_STAT_CATEGORIES = [
  { key: 'cancer', label: '암', description: '암·제자리암·경계성종양', group: '인기 상담' },
  { key: 'brain', label: '뇌', description: '뇌혈관·치매·파킨슨·뇌전증', group: '3대 질병' },
  { key: 'heart', label: '심장', description: '허혈성심장질환·심장질환', group: '3대 질병' },
  { key: 'fetus_child', label: '태아·어린이', description: '선천이상·신생아·소아 호흡기', group: '태아보험' },
  { key: 'child_sales', label: '어린이 영업 포인트', description: '수족구·가와사키·중이염·탈장', group: '태아보험' },
  { key: 'women', label: '여성질환', description: '자궁·난소·유방 양성질환', group: '성별 특화' },
  { key: 'chronic', label: '만성질환', description: '당뇨·고혈압·간·폐·신장', group: '생활질환' },
  { key: 'eye_dental_ent', label: '눈·치아·이비인후', description: '백내장·치아·중이염', group: '빈번 문의' },
  { key: 'injury', label: '상해', description: '손상·중독·골절·화상', group: '상해 담보' },
]

function formatCount(value, suffix = '건') {
  return `${Math.round(Number(value) || 0).toLocaleString('ko-KR')}${suffix}`
}

function formatNumber(value) {
  return Math.round(Number(value) || 0).toLocaleString('ko-KR')
}

function statLabel(stat) {
  return [stat?.providerTypeLabel, stat?.visitTypeLabel].filter(Boolean).join(' · ') || '전체'
}

function safeNumber(value) {
  return Number(value) || 0
}

function formatRank(value) {
  const n = Math.round(Number(value) || 0)
  return n > 0 ? `${n.toLocaleString('ko-KR')}위` : '순위 없음'
}

function compactCodeList(codes, limit = 3) {
  const arr = Array.from(new Set((Array.isArray(codes) ? codes : [])
    .map((code) => String(code || '').trim())
    .filter((code) => code && code !== '-')))
  if (arr.length === 0) return { text: '-', count: 0 }
  const head = arr.slice(0, limit).join(', ')
  return {
    text: arr.length > limit ? `${head} 외 ${arr.length - limit}개` : head,
    count: arr.length,
  }
}

function compactCoverageNames(items, limit = 6) {
  const names = Array.from(new Set((Array.isArray(items) ? items : [])
    .map((item) => String(item?.riderTypeName || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)))
  const visibleNames = names.slice(0, limit)
  return {
    names: visibleNames,
    text: visibleNames.join(' · '),
    fullText: names.join(', '),
    extraCount: Math.max(0, names.length - visibleNames.length),
    count: names.length,
  }
}

function safeDownloadName(value) {
  return String(value || '질병_진료비_인사이트')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 90)
}

function preparePngClone(clone) {
  clone.querySelectorAll('[data-png-exclude="true"]').forEach((node) => node.remove())
  clone.style.maxHeight = 'none'
  clone.style.height = 'auto'
  clone.style.overflow = 'visible'
  clone.style.overflowY = 'visible'
  clone.style.boxShadow = 'none'
  clone.querySelectorAll('[class*="overflow-y-auto"], [class*="max-h"]').forEach((node) => {
    node.style.maxHeight = 'none'
    node.style.height = 'auto'
    node.style.overflow = 'visible'
    node.style.overflowY = 'visible'
  })
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function waitForRenderFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

async function downloadElementAsPng(element, fileName) {
  if (!element) throw new Error('캡처할 영역이 없습니다.')
  if (document.fonts?.ready) await document.fonts.ready

  const sourceRect = element.getBoundingClientRect()
  const captureWidth = Math.max(320, Math.ceil(sourceRect.width || element.scrollWidth || 960))
  await waitForRenderFrame()

  const baseOptions = {
    backgroundColor: '#ffffff',
    logging: false,
    scale: Math.min(2, Math.max(1, window.devicePixelRatio || 1)),
    useCORS: true,
    windowWidth: Math.max(document.documentElement.scrollWidth, captureWidth),
    windowHeight: Math.max(document.documentElement.scrollHeight, element.scrollHeight, Math.ceil(sourceRect.height || 1)),
    onclone: (clonedDocument) => {
      const cloned = clonedDocument.querySelector('[data-disease-detail-capture="true"]')
      if (!cloned) return
      preparePngClone(cloned)
      cloned.style.width = `${captureWidth}px`
      cloned.style.maxWidth = 'none'
    },
  }

  let lastError = null
  for (const foreignObjectRendering of [true, false]) {
    try {
      const canvas = await html2canvas(element, { ...baseOptions, foreignObjectRendering })
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!blob) throw new Error('PNG 파일을 만들 수 없습니다.')
      downloadBlob(blob, fileName)
      return
    } catch (err) {
      lastError = err
    }
  }
  throw lastError || new Error('PNG 파일을 만들 수 없습니다.')
}

function getBestRank(stats) {
  const ranks = stats.map((stat) => safeNumber(stat.rank)).filter((rank) => rank > 0)
  return ranks.length > 0 ? Math.min(...ranks) : null
}

function reportWidth(value, maxValue, min = 6) {
  const max = safeNumber(maxValue)
  if (!max) return 0
  const width = Math.round((safeNumber(value) / max) * 100)
  if (width <= 0) return 0
  return Math.max(min, Math.min(100, width))
}

const METRIC_TONES = {
  blue: {
    box: 'border-blue-100 bg-blue-50',
    icon: 'bg-blue-600 text-white',
    label: 'text-blue-700',
    value: 'text-blue-950',
  },
  emerald: {
    box: 'border-emerald-100 bg-emerald-50',
    icon: 'bg-emerald-600 text-white',
    label: 'text-emerald-700',
    value: 'text-emerald-950',
  },
  amber: {
    box: 'border-amber-100 bg-amber-50',
    icon: 'bg-amber-500 text-white',
    label: 'text-amber-700',
    value: 'text-amber-950',
  },
  rose: {
    box: 'border-rose-100 bg-rose-50',
    icon: 'bg-rose-600 text-white',
    label: 'text-rose-700',
    value: 'text-rose-950',
  },
  slate: {
    box: 'border-slate-200 bg-white',
    icon: 'bg-slate-900 text-white',
    label: 'text-slate-500',
    value: 'text-slate-950',
  },
}

function MetricTile({ label, value, sub, icon: Icon, tone = 'slate', emphasis = false }) {
  const style = METRIC_TONES[tone] || METRIC_TONES.slate
  return (
    <div className={`min-w-0 rounded border p-3 ${style.box}`}>
      <div className="flex items-center gap-2">
        {Icon ? (
          <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded ${style.icon}`}>
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
        <div className="min-w-0">
          <div className={`truncate text-xs font-bold leading-5 ${style.label}`}>{label}</div>
          <div className={`mt-0.5 break-words font-bold leading-tight tabular-nums ${emphasis ? 'text-xl' : 'text-lg'} ${style.value}`}>
            {value}
          </div>
        </div>
      </div>
      {sub ? <div className="mt-1 break-keep text-xs leading-5 text-slate-500">{sub}</div> : null}
    </div>
  )
}

function EmptyPanel({ children }) {
  return (
    <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
      {children}
    </div>
  )
}

function ReportSectionTitle({ icon: Icon, children, meta }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-sm font-bold leading-6 text-slate-900">
        {Icon ? <Icon className="h-4 w-4 text-blue-600" /> : null}
        <span>{children}</span>
      </div>
      {meta ? <div className="text-xs leading-5 text-slate-500">{meta}</div> : null}
    </div>
  )
}

function DiseaseContentBlock({ title, children, icon: Icon, tone = 'slate' }) {
  if (!children) return null
  const style = METRIC_TONES[tone] || METRIC_TONES.slate
  return (
    <div className={`min-w-0 rounded border p-3 ${style.box}`}>
      <div className="flex items-center gap-2">
        {Icon ? (
          <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded ${style.icon}`}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        ) : null}
        <div className={`text-xs font-bold leading-5 ${style.label}`}>{title}</div>
      </div>
      <div className="mt-2 break-keep text-sm leading-6 text-slate-700">{children}</div>
    </div>
  )
}

function StatComparisonCard({ stat, maxCost }) {
  const width = reportWidth(stat.totalCost, maxCost)
  return (
    <div className="rounded border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase leading-5 tracking-normal text-slate-500">비교 기준</div>
          <div className="mt-0.5 truncate text-sm font-bold text-slate-900">{statLabel(stat)}</div>
          <div className="mt-0.5 text-xs leading-5 text-slate-500">전국 순위 {formatRank(stat.rank)}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-xs font-bold leading-5 text-slate-500">총진료비</div>
          <div className="mt-0.5 text-sm font-bold tabular-nums text-slate-950">{formatWon(stat.totalCost)}</div>
          <div className="mt-0.5 text-xs leading-5 text-slate-500">1인 평균 {formatWon(stat.avgCost)}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs leading-5 text-slate-500">
        <span>총진료비 규모</span>
        <span>{width}%</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded bg-slate-100">
        <div className="h-full rounded bg-blue-600" style={{ width: `${width}%` }} />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs leading-5 text-slate-500">
        <div>
          환자 <span className="font-semibold text-slate-800">{formatCount(stat.patientCount, '명')}</span>
        </div>
        <div>
          진료 <span className="font-semibold text-slate-800">{formatCount(stat.visitCount)}</span>
        </div>
        <div>
          내원 <span className="font-semibold text-slate-800">{formatCount(stat.careDays, '일')}</span>
        </div>
      </div>
    </div>
  )
}

function DiseaseDetailModal({ detail, loading, selectedItem, onClose }) {
  const { show } = useToast()
  const captureRef = React.useRef(null)
  const [downloading, setDownloading] = React.useState(false)
  const disease = detail?.diseaseInfo || {}
  const diseaseContent = detail?.diseaseContent || {}
  const stats = Array.isArray(detail?.statistics?.items) ? detail.statistics.items : []
  const statSummary = detail?.statistics?.summary || {}
  const compensationTypes = Array.isArray(detail?.compensationTypes) ? detail.compensationTypes : []
  const titleCode = detail?.kcdCode || selectedItem?.kcdCode || selectedItem?.kcdCode3 || '-'
  const titleName = disease?.nameKo || selectedItem?.diseaseName || selectedItem?.sourceDiseaseName || '질병명 없음'
  const bestRank = getBestRank(stats)
  const maxCost = Math.max(...stats.map((stat) => safeNumber(stat.totalCost)), 0)
  const topStat = stats.reduce((best, stat) => (safeNumber(stat.totalCost) > safeNumber(best?.totalCost) ? stat : best), null)
  const visibleStats = stats.slice(0, 4)
  const coverageSummary = compactCoverageNames(compensationTypes)
  const diseaseTitle = diseaseContent.titleKo || disease.nameKo || titleName
  const heroSummary = diseaseContent.summary || `${titleName}의 공개 진료비 통계와 관련 보장 항목을 요약했습니다.`
  const relatedCodeSummary = compactCodeList([
    titleCode,
    detail?.kcdCode,
    selectedItem?.kcdCode,
    selectedItem?.kcdCode3,
    disease?.code,
    disease?.code3,
    diseaseContent?.kcdPattern,
    ...stats.map((stat) => stat?.kcdCode),
  ])

  const handleDownloadPng = async () => {
    if (!captureRef.current || loading || downloading) return
    setDownloading(true)
    try {
      const fileName = `${safeDownloadName(`${titleName}_${titleCode}_진료비_인사이트`)}.png`
      await downloadElementAsPng(captureRef.current, fileName)
      show('PNG 파일을 다운로드했습니다.', 'success')
    } catch {
      show('PNG 다운로드에 실패했습니다.', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true">
      <div ref={captureRef} data-disease-detail-capture="true" className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-blue-50 px-2 py-1 font-mono text-xs font-bold text-blue-700">{titleCode}</span>
              <h2 className="text-lg font-bold text-slate-900">[{titleName}] 고객 제시용 진료비 인사이트</h2>
            </div>
            {relatedCodeSummary.count > 0 ? (
              <div className="mt-1 text-xs text-slate-500">관련 질병코드 {relatedCodeSummary.text}</div>
            ) : null}
            <div className="mt-2 w-fit max-w-full rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold leading-7 text-amber-800">
              건강보험 급여 진료비 기준이며 비급여·전액본인부담은 제외됩니다.
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-png-exclude="true">
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded border border-blue-200 px-3 text-xs font-bold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleDownloadPng}
              disabled={loading || downloading}
            >
              <Download className="h-4 w-4" />
              {downloading ? '저장 중' : 'PNG'}
            </button>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded border text-slate-500 hover:bg-slate-50"
              onClick={onClose}
              aria-label="닫기"
              title="닫기"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="rounded border bg-white p-8 text-sm text-slate-500">상세 정보를 불러오는 중입니다.</div>
          ) : (
            <div className="space-y-4">
              <section className="overflow-hidden rounded border border-slate-200 bg-white">
                <div className="bg-slate-950 px-5 py-5 text-white">
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-3 inline-flex items-center gap-2 rounded bg-white/10 px-2.5 py-1 text-xs font-semibold text-blue-100">
                        <HeartPulse className="h-3.5 w-3.5" />
                        질병 진료비 요약
                      </div>
                      <h3 className="break-keep text-2xl font-bold leading-snug text-white md:text-3xl">{diseaseTitle}</h3>
                      <p className="mt-3 max-w-3xl break-keep text-sm leading-6 text-slate-200">{heroSummary}</p>
                    </div>
                    <div className="grid shrink-0 grid-cols-2 gap-2 sm:min-w-[360px]">
                      <MetricTile
                        icon={Coins}
                        label="전국 총진료비"
                        value={formatWon(statSummary.totalCost)}
                        sub={statSummary.benefitCost ? `보험자부담 ${formatWon(statSummary.benefitCost)}` : '전국 공개 데이터'}
                        tone="amber"
                        emphasis
                      />
                      <MetricTile
                        icon={Award}
                        label="전국 순위"
                        value={bestRank ? formatRank(bestRank) : '순위 없음'}
                        sub={topStat ? statLabel(topStat) : '통계 없음'}
                        tone="emerald"
                        emphasis
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 border-t border-slate-200 bg-slate-50 p-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricTile
                    icon={UsersRound}
                    label="환자 수"
                    value={formatCount(statSummary.patientCount, '명')}
                    sub={`진료 ${formatCount(statSummary.visitCount)}`}
                    tone="blue"
                  />
                  <MetricTile
                    icon={ReceiptText}
                    label="1인 평균 진료비"
                    value={formatWon(statSummary.avgCost)}
                    sub="총진료비 ÷ 환자 수"
                    tone="slate"
                  />
                  <MetricTile
                    icon={Activity}
                    label="진료 건수"
                    value={formatCount(statSummary.visitCount)}
                    sub={topStat ? `${statLabel(topStat)} 포함` : '전국 공개 데이터'}
                    tone="rose"
                  />
                  <MetricTile
                    icon={FileText}
                    label="관련 질병코드"
                    value={relatedCodeSummary.text}
                    sub={relatedCodeSummary.count > 1 ? `${relatedCodeSummary.count}개 코드 연결` : 'KCD 기준 코드'}
                    tone="emerald"
                  />
                </div>

                <div className="border-t border-emerald-100 bg-emerald-50 px-4 py-3">
                  <div className="flex min-w-0 flex-wrap items-stretch gap-2 text-sm">
                    <span className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded border border-emerald-200 bg-white px-3 text-sm font-bold leading-6 text-emerald-800">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      관련 보장 항목
                    </span>
                    {coverageSummary.count > 0 ? (
                      <>
                        <div className="flex min-w-0 flex-wrap items-stretch gap-2 font-semibold text-emerald-950" title={coverageSummary.fullText}>
                          {coverageSummary.names.map((name) => (
                            <span key={name} className="inline-flex min-h-10 items-center whitespace-nowrap rounded border border-emerald-200 bg-white px-3 text-sm leading-6 text-emerald-950">
                              {name}
                            </span>
                          ))}
                        </div>
                        {coverageSummary.extraCount > 0 ? (
                          <span className="inline-flex min-h-10 shrink-0 items-center text-xs font-bold leading-5 text-emerald-700">외 {coverageSummary.extraCount}개</span>
                        ) : null}
                      </>
                    ) : (
                      <div className="min-w-0 truncate text-xs font-semibold text-slate-500">연결된 보장 항목이 없습니다.</div>
                    )}
                  </div>
                </div>
              </section>

              <section className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.05fr_0.95fr]">
                <div className="rounded border border-slate-200 bg-white p-4">
                  <ReportSectionTitle icon={LineChart} meta="막대 길이 = 총진료비 규모">
                    진료 유형별 총진료비 비교
                  </ReportSectionTitle>
                  {stats.length > 0 ? (
                    <>
                      <div className="mb-3 grid grid-cols-1 gap-2 rounded border border-blue-100 bg-blue-50 p-3 text-xs md:grid-cols-3">
                        <div>
                          <div className="font-bold text-blue-700">비교대상</div>
                          <div className="mt-0.5 text-slate-700">의과/한방 · 입원/외래</div>
                        </div>
                        <div>
                          <div className="font-bold text-blue-700">핵심값</div>
                          <div className="mt-0.5 text-slate-700">총진료비와 1인 평균</div>
                        </div>
                        <div>
                          <div className="font-bold text-blue-700">막대 의미</div>
                          <div className="mt-0.5 text-slate-700">가장 큰 총진료비 대비 비율</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {visibleStats.map((stat, idx) => (
                          <StatComparisonCard key={`${stat.kcdCode}-${stat.providerType}-${stat.inpatientOutpatient}-${idx}`} stat={stat} maxCost={maxCost} />
                        ))}
                      </div>
                    </>
                  ) : (
                    <EmptyPanel>{detail?.statistics?.message || '해당 질병코드로 연결된 HIRA 통계가 없습니다.'}</EmptyPanel>
                  )}
                </div>

                <div className="rounded border border-slate-200 bg-white p-4">
                  <ReportSectionTitle icon={ClipboardCheck}>
                    고객 확인 포인트
                  </ReportSectionTitle>
                  <div className="grid grid-cols-1 gap-2">
                    <DiseaseContentBlock title="주요 양상" icon={Stethoscope} tone="blue">{diseaseContent.symptoms}</DiseaseContentBlock>
                    <DiseaseContentBlock title="확인 포인트" icon={ClipboardCheck} tone="amber">{diseaseContent.checkPoints}</DiseaseContentBlock>
                    <DiseaseContentBlock title="보험 검토 메모" icon={ShieldCheck} tone="emerald">{diseaseContent.careNote}</DiseaseContentBlock>
                    {!diseaseContent.symptoms && !diseaseContent.checkPoints && !diseaseContent.careNote ? (
                      <EmptyPanel>고객에게 확인할 질병별 메모가 아직 없습니다.</EmptyPanel>
                    ) : null}
                  </div>
                </div>
              </section>

            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function DiseaseMedicalFeeStatsPage({ embedded = false }) {
  const { show } = useToast()
  const [q, setQ] = useState('')
  const [appliedQ, setAppliedQ] = useState('')
  const [providerType, setProviderType] = useState('')
  const [inpatientOutpatient, setInpatientOutpatient] = useState('')
  const [category, setCategory] = useState('')
  const [categories, setCategories] = useState(FALLBACK_STAT_CATEGORIES)
  const [sort, setSort] = useState('rank')
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)

  const params = useMemo(() => ({
    q: appliedQ,
    category,
    providerType,
    inpatientOutpatient,
    sort,
    page,
    size: 20,
  }), [appliedQ, category, providerType, inpatientOutpatient, sort, page])

  useEffect(() => {
    let cancelled = false
    const loadCategories = async () => {
      try {
        const res = await api.get('/health/hira-disease-stats/categories')
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const rows = Array.isArray(payload?.items) ? payload.items : []
        if (!cancelled && rows.length > 0) setCategories(rows)
      } catch {
        if (!cancelled) setCategories(FALLBACK_STAT_CATEGORIES)
      }
    }
    loadCategories()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await api.get('/health/hira-disease-stats', { params })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!cancelled) setData(payload || null)
      } catch {
        if (!cancelled) {
          setData(null)
          show('질병 진료비 통계를 불러오지 못했습니다.', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [params, show])

  const submitSearch = (event) => {
    event.preventDefault()
    setPage(1)
    setAppliedQ(q.trim())
  }

  const resetPageWith = (setter) => (event) => {
    setter(event.target.value)
    setPage(1)
  }

  const selectCategory = (nextCategory) => {
    setCategory(nextCategory)
    setPage(1)
  }

  const openDetail = async (item) => {
    const code = item?.kcdCode || item?.kcdCode3
    if (!code) {
      show('상세 조회할 KCD 코드가 없습니다.', 'warning')
      return
    }
    setSelectedItem(item)
    setDetail(null)
    setDetailOpen(true)
    setDetailLoading(true)
    try {
      const res = await api.get(`/health/hira-disease-stats/${encodeURIComponent(code)}/detail`, {
        params: { providerType, inpatientOutpatient },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setDetail(payload || null)
    } catch {
      setDetail(null)
      show('질병 상세 인사이트를 불러오지 못했습니다.', 'error')
    } finally {
      setDetailLoading(false)
    }
  }

  const items = Array.isArray(data?.items) ? data.items : []
  const total = Number(data?.total || 0)
  const pageSize = Number(data?.size || 20)
  const maxPage = Math.max(1, Math.ceil(total / pageSize))
  const hasActiveRelease = Boolean(data && data.status !== 'NO_ACTIVE_RELEASE')

  return (
    <div className="space-y-4">
      {!embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-normal text-slate-900">질병 진료비 통계</h1>
          </div>
          <Link className="rounded border px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" to="/hidden-insurance-money">
            숨은보험금찾기
          </Link>
        </div>
      ) : null}

      <section className="rounded border bg-white p-4">
        <form className="flex flex-wrap items-end gap-3" onSubmit={submitSearch}>
          <div className="min-w-[240px] flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">질병명 또는 KCD 코드</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                className="w-full rounded border py-2 pl-8 pr-3 text-sm focus:border-slate-500 focus:outline-none"
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="예: M54, 허리통증, 암"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">진료 구분</label>
            <select
              className="min-w-[120px] rounded border px-2 py-2 text-sm"
              value={providerType}
              onChange={resetPageWith(setProviderType)}
            >
              {PROVIDERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">입원/외래</label>
            <select
              className="min-w-[120px] rounded border px-2 py-2 text-sm"
              value={inpatientOutpatient}
              onChange={resetPageWith(setInpatientOutpatient)}
            >
              {VISIT_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">정렬</label>
            <select
              className="min-w-[130px] rounded border px-2 py-2 text-sm"
              value={sort}
              onChange={resetPageWith(setSort)}
            >
              {SORTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-1.5 rounded bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4" />
            조회
          </button>
        </form>
      </section>

      <section className="rounded border bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`rounded border px-3 py-1.5 text-sm font-semibold ${category === '' ? 'border-slate-900 bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
            onClick={() => selectCategory('')}
          >
            전체
          </button>
          {categories.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`rounded border px-3 py-1.5 text-sm ${category === item.key ? 'border-blue-700 bg-blue-700 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => selectCategory(item.key)}
              title={item.description || item.label}
            >
              {item.label}
            </button>
          ))}
        </div>
        {category ? (
          <div className="mt-3 text-xs text-slate-500">
            {categories.find((item) => item.key === category)?.description || '선택한 카테고리'} · 총 {formatNumber(total)}개 항목
          </div>
        ) : (
          <div className="mt-3 text-xs text-slate-500">총 {formatNumber(total)}개 항목</div>
        )}
      </section>

      {!loading && data?.status === 'NO_ACTIVE_RELEASE' ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          활성화된 진료비통계지표가 없습니다.
        </div>
      ) : null}

      {loading ? (
        <div className="rounded border bg-white p-8 text-sm text-slate-500">통계를 불러오는 중입니다.</div>
      ) : null}

      {!loading && hasActiveRelease ? (
        <section className="overflow-hidden rounded border bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2">순위</th>
                  <th className="px-3 py-2">KCD</th>
                  <th className="px-3 py-2">질병명</th>
                  <th className="px-3 py-2">구분</th>
                  <th className="px-3 py-2 text-right">환자 수</th>
                  <th className="px-3 py-2 text-right">진료 건수</th>
                  <th className="px-3 py-2 text-right">총진료비</th>
                  <th className="px-3 py-2 text-right">1인 평균</th>
                  <th className="px-3 py-2 text-right">상세</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td className="px-3 py-8 text-center text-slate-500" colSpan={9}>조회 조건에 맞는 통계가 없습니다.</td>
                  </tr>
                ) : null}
                {items.map((item, idx) => (
                  <tr key={`${item.kcdCode}-${item.providerType}-${item.inpatientOutpatient}-${idx}`} className="border-t align-top">
                    <td className="px-3 py-3 text-slate-700">{item.rank ? `${item.rank}위` : '-'}</td>
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900">{item.kcdCode || item.kcdCode3 || '-'}</div>
                      {item.kcdCode3 && item.kcdCode3 !== item.kcdCode ? <div className="text-xs text-slate-500">{item.kcdCode3}</div> : null}
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{item.diseaseName || '-'}</div>
                      {item.sourceDiseaseName && item.sourceDiseaseName !== item.diseaseName ? (
                        <div className="mt-0.5 text-xs text-slate-500">통계 원문: {item.sourceDiseaseName}</div>
                      ) : null}
                      {Array.isArray(item.riskTags) && item.riskTags.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {item.riskTags.map((tag) => (
                            <span key={tag} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">{tag}</span>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{item.providerTypeLabel || '-'}</div>
                      <div className="text-xs text-slate-500">{item.visitTypeLabel || '-'}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-900">{formatCount(item.patientCount, '명')}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-900">{formatCount(item.visitCount)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold text-slate-900">{formatWon(item.totalCost)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-900">{formatWon(item.avgCost)}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        onClick={() => openDetail(item)}
                      >
                        <Info className="h-3.5 w-3.5" />
                        상세
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-sm">
            <div className="text-slate-500">총 {formatNumber(total)}개 항목</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage((v) => Math.max(1, v - 1))}
                disabled={page <= 1}
                aria-label="이전 페이지"
                title="이전 페이지"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[72px] text-center text-slate-700">{page} / {maxPage}</span>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage((v) => Math.min(maxPage, v + 1))}
                disabled={page >= maxPage}
                aria-label="다음 페이지"
                title="다음 페이지"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {detailOpen ? (
        <DiseaseDetailModal
          detail={detail}
          loading={detailLoading}
          selectedItem={selectedItem}
          onClose={() => setDetailOpen(false)}
        />
      ) : null}
    </div>
  )
}
